import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import log from 'loglevel';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import EmailEditor from '@/shared/components/EmailEditor';
import { injectStateService } from '@/shared/services/StateService';
import handleHttpError from '@/shared/services/HandleHttpError';
import moment from 'moment';
import LoadingAnimation from '../LoadingAnimation';
import EmailRecipients from './EmailRecipients';
import {
  combineForRequest,
  createEmptyRecipients,
  getEmailRecipients,
  sanitizeRecipients,
  validateRecipients,
} from './emailHelpers';

import messages from './emailMessages';

class ReplyToEmailModal extends Component {
  state = {
    email: undefined,
    tags: [],
    recipients: createEmptyRecipients(),
    emailAttachments: [],
  };

  componentDidMount() {
    this.form = React.createRef();
    this.initializeModal();
  }

  static propTypes = {
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    documentId: PropTypes.string.isRequired,
    replyAll: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    show: false,
  };

  initializeModal = () => {
    const { documentId, relationId, intl, replyAll } = this.props;

    const emailContentPromise = api.base.get(
      `${config.appUrls.documents}/api/emails/ReplyToInfo/${documentId}?replyAll=${replyAll}`,
    );
    const emailAddressesPromise = getEmailRecipients(relationId, null, intl);

    this.setState({ loading: true });
    Promise.all([emailContentPromise, emailAddressesPromise])
      .then(([{ data: forwardInfo }, { addresses, employees }]) => {
        this.setState(({ recipients }) => ({
          loading: false,
          recipients: sanitizeRecipients({
            ...recipients,
            to: forwardInfo.to,
            cc: forwardInfo.cc,
            bcc: forwardInfo.bcc,
          }),
          companyId: forwardInfo.companyId,
          tags: forwardInfo.tags,
          relations: forwardInfo.relations,
          email: {
            title: forwardInfo.subject,
            initialValue: forwardInfo.content,
            content: forwardInfo.content,
            documentDate: moment.utc(),
          },
          emailAttachments: [],
          addresses,
          employees,
        }));
      })
      .catch(err => {
        handleHttpError(intl)(err);
        this.setState({ loading: false });
      });
  };

  validateEmail = value => {
    if (!value) return false;
    return /^[^@]+@.+\..+$/.test(value);
  };

  onSave = () => {
    const {
      email,
      emailAttachments,
      recipients,
      selectedTemplate,
      companyId,
      tags,
      relations,
    } = this.state;
    const { intl } = this.props;
    const isFormValid = this.validate();
    if (!validateRecipients(recipients)) {
      ToastrService.error(intl.formatMessage(messages.msgInvalidEmailAddress));
      return false;
    }

    let invalidEmailBody = false;

    if (
      !email.content ||
      // regex to remove line breaks
      email.content.replace(/(\r\n|\n|\r)/gm, '') === '<p></p>'
    ) {
      invalidEmailBody = true;
    }

    if (invalidEmailBody || !isFormValid) {
      ToastrService.error(intl.formatMessage(messages.SubjectBodyRecipientsAreRequired));
      return Promise.reject(new Error('invalid form'));
    }

    const emailViewModel = {
      ...combineForRequest(recipients),
      body: email.content,
      subject: email.title,
      attachmentDocumentIds: emailAttachments.map(x => x.id) || [],
      relationIds: relations,
      companyId,
      emailLinkType: 4,
      documentDate: email.documentDate,
      emailTemplateId: selectedTemplate,
      tags,
    };

    return api.base
      .post(`${config.appUrls.documents}/api/emails/CreateAndSend`, emailViewModel)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.EmailSent));
        this.closeModal();
      })
      .catch(handleHttpError(intl));
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  emailTitleChanged = title => {
    const { email } = this.state;

    this.setState({
      email: {
        ...email,
        title,
      },
    });
  };

  onEditorStateChange = content => {
    const { email } = this.state;
    this.setState({
      email: {
        ...email,
        content,
      },
    });
  };

  renderSingleEmail = email => {
    const { emailAttachments, relations } = this.state;
    let documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=9&informQueues=false&recognitionStatus=4`;

    documentUploadEndpoint += `&${relations
      .map((n, index) => `relationIds[${index}]=${n}`)
      .join('&')}`;

    return (
      <EmailEditor
        title={email.title}
        onTitleChanged={title => this.emailTitleChanged(title)}
        initialValue={email.initialValue}
        onContentChanged={content => this.onEditorStateChange(content)}
        onAttachmentUploaded={(error, file) => this.onDocumentUploaded(error, file)}
        attachmentUploadEndpoint={documentUploadEndpoint}
        attachments={emailAttachments}
        onDeleteAttachment={this.deleteAttachment}
        documentDate={email.documentDate}
        onDocumentDateChanged={documentDate =>
          this.setState({
            email: {
              ...email,
              documentDate,
            },
          })
        }
      />
    );
  };

  onDocumentUploaded = (error, document) => {
    const { intl } = this.props;

    if (error) {
      log.error(error);
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }

    const { emailAttachments } = this.state;

    if (emailAttachments.some(x => x.id === document.id)) return;

    this.setState(state => ({
      ...state,
      emailAttachments: [document, ...state.emailAttachments],
    }));
  };

  // when deleting an attachment when forwarding an email we wont delete the original attachment from our system but rather not send it with the new email
  deleteAttachment = attachment => {
    this.setState(state => ({
      emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
    }));
  };

  validate() {
    const { email } = this.state;
    if (!email) return false;
    if (!this.form || !this.form.current) return false;

    return this.form.current.reportValidity();
  }

  handleRecipientChange = (type, newValues) => {
    this.setState(({ recipients }) => ({
      recipients: {
        ...recipients,
        [type]: newValues || [],
      },
    }));
  };
  render() {
    const { show, intl } = this.props;
    const { email, addresses, recipients, loading, employees } = this.state;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.Reply)}
        show={show}
        onClose={this.closeModal}
        onSave={this.onSave}
        size="lg"
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              {loading && <LoadingAnimation />}
              {email && (
                <EmailRecipients
                  value={recipients}
                  addresses={addresses}
                  employees={employees}
                  onChange={this.handleRecipientChange}
                />
              )}

              {email && this.renderSingleEmail(email)}
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}
export default injectStateService(ReplyToEmailModal);
